import {api} from "../api";

import {RecordingInfoPanel} from "../recording/recordingInfoPanel";
import {BudgetCalculator, ConsumedBudgetResult} from "../budget/budgetCalculator";
import {htmlEscape} from "../utils/string";
import {hoursToMillis} from "@/shared/utils/misc";
import {SecurityStatus} from "@/shared/securityStatus";
import {formatDuration} from "@/shared/utils/datefunctions";
import {RunningProjectTime, TaskUserBudgetInfo} from "@/shared/timr";

export class ProjectTimeInfoPanel extends RecordingInfoPanel {

	private static MINIMUM_UPDATE_INTERVAL = 60; // seconds

	private lastTaskId: number;
	private lastUserId: number;
	private lastBudgetPlanningType: string;
	private lastUpdateTimestamp: number;
	private getTaskUserBudgetInfoRequest: JQuery.jqXHR;

	/**
	 * Updates data of the info panel. This includes:
	 * projectTimeTaskDescription: description (internal) of the task - visible if task description (internal) of the task is not empty
	 * projectTimeTaskBudget: contains budget info (bar, status) - visible if feature allowed and task budget configured
	 * projectTimeTipField: info message for trial plan - visible if trial plan and no visible projectTimeTaskDescription or projectTimeTaskBudget
	 * @param taskId
	 * @param description
	 * @param runningProjectTime
	 * @param userId
	 * @param forceUpdate
	 */
	public update(taskId: number, description: string, runningProjectTime?: RunningProjectTime, userId: number = null, forceUpdate = true) {
		const hasDescription = description != null && $.trim(description).length > 0;
		if (hasDescription) {
			description = htmlEscape(description);
			description = description.replace(/\n/g, "<br/>");
			this.infoPanel.find(".projectTimeTaskDescription")
				.html(description)
				.show();
		} else {
			this.infoPanel.find(".projectTimeTaskDescription").text("");
		}

		// checks whether to query the budget information and update the fields
		if (this.shouldQueryBudgetInfo(taskId, userId, forceUpdate)) {
			if (taskId != null) {
				this.getTaskUserBudgetInfoRequest?.abort();
				this.getTaskUserBudgetInfoRequest = api("recording/getTaskUserBudgetInfo.json")
					.get({taskId: taskId, userId: userId})
					.done((data: {
						taskUserBudgetInfo: TaskUserBudgetInfo
					}) => this.updateBudgetInfo(data.taskUserBudgetInfo, hasDescription, runningProjectTime));
			} else {
				this.updateBudgetInfo(null, hasDescription, runningProjectTime);
			}
		} else {
			this.infoPanel.find(".projectTimeTipField").toggle(!hasDescription);
		}
		this.lastTaskId = taskId;
		this.lastUserId = userId;
		this.onVisibilityChanged();
	}

	public reset() {
		this.infoPanel.find(".projectTimeTipField").hide();
		this.infoPanel.find(".projectTimeTaskBudget").hide();
		this.lastTaskId = null;
		this.lastUserId = null;
		this.lastBudgetPlanningType = null;
		this.lastUpdateTimestamp = null;
		this.onVisibilityChanged();
	}

	/**
	 * Returns boolean if at least one element of the info panel is visible.
	 * The fields are projectTimeTipField, projectTimeTaskBudget or projectTimeTaskDescription (visible if not blank)
	 */
	public isVisible(): boolean {
		return this.infoPanel.find(".projectTimeTipField").css("display") === "block" ||
			this.infoPanel.find(".projectTimeTaskBudget").css("display") === "block" ||
			this.infoPanel.find(".projectTimeTaskDescription").text() !== "";
	}

	private shouldQueryBudgetInfo(taskId: number, userId: number, forceUpdate: boolean) {
		if (!SecurityStatus.isBudgetsAllowed() || !globals.showBudgetInformationForUsers) {
			return false;
		}
		const currentTimestamp = new Date().getTime();
		if (forceUpdate) {
			this.lastUpdateTimestamp = currentTimestamp;
			return true;
		}
		if (this.lastTaskId == null && taskId == null && this.lastUserId == null && userId == null) {
			return false;
		}
		if (this.lastTaskId == null && taskId != null || this.lastTaskId != null && taskId == null ||
			this.lastUserId == null && userId != null || this.lastUserId != null && userId == null) {
			this.lastUpdateTimestamp = currentTimestamp;
			return true;
		}
		if (this.lastTaskId !== taskId || this.lastUserId !== userId) {
			this.lastUpdateTimestamp = currentTimestamp;
			return true;
		}
		if (this.lastBudgetPlanningType == null) {
			return false;
		}
		if (this.lastUpdateTimestamp == null || (currentTimestamp - this.lastUpdateTimestamp) / 1000 >= ProjectTimeInfoPanel.MINIMUM_UPDATE_INTERVAL) {
			this.lastUpdateTimestamp = currentTimestamp;
			return true;
		}
		return false;
	}

	/**
	 * Updates the budget information that contains the bar and the text about the current budget and the visibility of the info panel
	 * For the bar, the data of the budget is first prepared and the length of the individual elements is adjusted afterwards
	 * If there are no budget hours planned, the budget container projectTimeTaskBudget will be hided
	 * @param taskUserBudgetInfo budget data
	 * @param hasDescription variable if task has a description
	 * @param runningProjectTime if there is a running project time
	 * @private
	 */
	private updateBudgetInfo(taskUserBudgetInfo: TaskUserBudgetInfo, hasDescription: boolean, runningProjectTime?: RunningProjectTime) {
		if (taskUserBudgetInfo?.hoursPlanned) {
			const consumedBudgetResult: ConsumedBudgetResult = BudgetCalculator.calculateConsumedBudget(taskUserBudgetInfo, runningProjectTime);
			this.infoPanel.find(".projectTimeTaskPlanned")
				.css("width", consumedBudgetResult.plannedPercentage + "%")
				.toggle(consumedBudgetResult.plannedPercentage > 0 && consumedBudgetResult.plannedPercentage < 100);

			this.infoPanel.find(".projectTimeTaskConsumed").css("width", consumedBudgetResult.consumedPercentage + "%");

			this.infoPanel.find(".projectTimeTaskExceeded").css("width", consumedBudgetResult.exceededPercentage + "%")
				.css("margin-left", consumedBudgetResult.plannedPercentage + "%")
				.toggle(consumedBudgetResult.exceededPercentage > 0);

			this.infoPanel.find(".projectTimeTaskRemaining").html(formatDuration(Math.max(0, hoursToMillis(consumedBudgetResult.hoursPlanned) - consumedBudgetResult.consumed)));
			this.infoPanel.find(".projectTimeTaskBudget").show();
			this.infoPanel.find(".projectTimeTipField").hide();
			this.lastBudgetPlanningType = taskUserBudgetInfo.budgetPlanningType;
		} else {
			this.infoPanel.find(".projectTimeTaskBudget").hide();
			this.infoPanel.find(".projectTimeTipField").toggle(!hasDescription);
			this.lastBudgetPlanningType = null;
		}
		this.onVisibilityChanged();
	}
}
